import {
    Box,
    Flex,
    Heading,
    SimpleGrid,
    Skeleton,
    Spinner,
    Stack,
    VStack,
} from '@chakra-ui/react';
import {
    Hide,
    PageLayout,
    Pagination,
    Show,
    useAppliedFilterLabel,
    useFacetFilter,
    usePaginationContext,
    useSearchQueryFilter,
    useServiceExclusivityFilter,
    useServiceFilter,
} from '@acp/core-ui';
import { useEffect } from 'react';
import { usePropertyManagerFilter } from '../../agency';
import { useReportedFindings } from '../hooks/use-reported-findings';
import { ReportedFindingsRow, ReportedFindingsRowHeader } from './cards/reported-findings-row';
import { useServicingCategoryFilter } from '../hooks/use-servicing-categories';

export function ReportedFindingsSkeleton({
    pageSize = 10,
}: {
    pageSize?: number;
}) {
    return (
        <VStack spacing={4} w="100%" alignItems="stretch">
            {new Array(pageSize).fill(0).map((_, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <Skeleton key={idx}>
                    <Box w="full" h="100px" />
                </Skeleton>
            ))}
        </VStack>
    );
}

export function ReportedFindingsTable() {
    const paginationProps = usePaginationContext();

    const { query } = useSearchQueryFilter();
    const { facets } = useFacetFilter();
    const { propertyManager } = usePropertyManagerFilter();
    const { services } = useServiceFilter();
    const { serviceExclusivity } = useServiceExclusivityFilter();
    const { servicingCategories } = useServicingCategoryFilter();

    const { data, isLoading } = useReportedFindings(
        {
            facets,
            services,
            serviceExclusivity,
            query,
            propertyManager,
            servicingCategories,
        },
        paginationProps.currentPage,
        paginationProps.pageSize
    );

    const filterLabel = useAppliedFilterLabel({
        totalResults: data?.total ?? null,
        unfilteredTotalResults: data?.unfilteredTotal ?? null,
        entitySingular: 'reported finding',
        entityPlural: 'reported findings',
    });

    useEffect(() => {
        if (data === undefined) return;

        paginationProps.setTotalResults(data.total);
    }, [data]);

    return (
        <VStack alignItems="flex-start" spacing={8}>
            <Stack
                direction={{ base: 'column', md: 'row' }}
                alignItems={{ base: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                w="full"
            >
                {!isLoading ? (
                    <>
                        <Heading size="md">{filterLabel}</Heading>
                        <Flex alignSelf="flex-end">
                            <Pagination {...paginationProps} />
                        </Flex>
                    </>
                ) : (
                    <Spinner ml={2} size="sm" color="gray.500" />
                )}
            </Stack>
            <Show above={PageLayout.Sidebar.Breakpoint}>
                <Box w="full">
                    <ReportedFindingsRowHeader />
                </Box>
            </Show>

            {isLoading && (<ReportedFindingsSkeleton pageSize={paginationProps.pageSize} />)}
            <Show above={PageLayout.Sidebar.Breakpoint}>
                <VStack spacing={4} w="full">
                    {data?.items.map((item) => (
                        <ReportedFindingsRow
                            key={`${item.property.compliancePropertyId}_${item.reportedFindings[0].problemRegister.serviceCode}`}
                            reportedFinding={item}
                        />
                    ))}
                </VStack>
            </Show>
            <Hide above={PageLayout.Sidebar.Breakpoint}>
                <SimpleGrid columns={[1, 1, 1, 2]} spacing={4} w="full">
                    {data?.items.map((item) => (
                        <ReportedFindingsRow
                            key={`${item.property.compliancePropertyId}_${item.reportedFindings[0].problemRegister.serviceCode}`}
                            reportedFinding={item}
                        />
                    ))}
                </SimpleGrid>
            </Hide>
        </VStack>
    );
}
